import React, { useContext, useState } from "react";
import { useLocale, WithLocale } from "@insightfulscience/i18";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import FsLightbox from "fslightbox-react";
import {
	VectorPageContactUs,
	VectorPageContainer,
	VectorPageImageWrapper,
	VectorPageListItem,
	VectorPageListWrapper,
	VectorPageParagraph,
	VectorPageParagraphWrapper,
	VectorPageSubTitle,
	VectorPageTable,
	VectorPageTableItem,
	VectorPageTableItemWrapper,
	VectorPageTabWrapper,
	VectorPageTitle,
	VectorPageTryButton,
	VectorPageTryButtonWrapper,
	VectorPageVideoContainer,
} from "@insightfulscience/atomic-react/r1/molecules/VectorNTI";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import SmartTabs from "@insightfulscience/atomic-react/utils/SmartTabs";
import Tabs from "@insightfulscience/atomic-react/Tabs";
import { Col, Row } from "@insightfulscience/atomic-react/Grid";
import Image from "@insightfulscience/atomic-react/Image";
import WistiaVideo from "@insightfulscience/atomic-react/r2/WistiaVideo";
import WistiaVideoEmbed from "@insightfulscience/atomic-react/r2/WistiaVideo/Embed";
import Line from "@insightfulscience/atomic-react/Line";
import BlockQuote, {
	BlockQuoteItem,
} from "@insightfulscience/atomic-react/r1/molecules/BlockQuote";
import LightBoxItem from "@insightfulscience/atomic-react/r2/LightBox/Item";
import AssetsProvider from "@insightfulscience/atomic-react/utils/AssetsProvider";
import Html from "@insightfulscience/smart-react/Html";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import SiteLayout from "../src/SiteLayout";
import resources from "../locales/vector-nti-page";

const VectorNTI = () => {
	const [t] = useLocale("vector-nti");

	const baseUrl = useContext(AssetsProvider.context);

	const vntiAprPreview = "/images/snapgene/vector-nti/VNTI_apr_preview.png";
	const vntiAprFull = `${baseUrl}/images/snapgene/vector-nti/VNTI_apr_full.png`;
	const vntiCepPreview = "/images/snapgene/vector-nti/VNTI_cep_preview.png";
	const vntiCepFull = `${baseUrl}/images/snapgene/vector-nti/VNTI_cep_full.png`;
	const vntiGbPreview = "/images/snapgene/vector-nti/VNTI_gb_preview.png";
	const vntiGbFull = `${baseUrl}/images/snapgene/vector-nti/VNTI_gb_full.png`;
	const vntiAdvancePreview = "/images/snapgene/vector-nti/VNTI_advance_preview.png";
	const vntiAdvanceFull = `${baseUrl}/images/snapgene/vector-nti/VNTI_advance_full.png`;
	const vntiExpressPreview = "/images/snapgene/vector-nti/VNTI_express_preview.png";
	const vntiExpressFull = `${baseUrl}/images/snapgene/vector-nti/VNTI_express_full.png`;
	const map = "/images/snapgene/vector-nti/map.png";

	const [lightboxController, setLightboxController] = useState({
		toggler: false,
		image: "",
	});

	const openLightboxOnSlide = (e, imageSrc) => {
		e.preventDefault();
		setLightboxController({
			toggler: !lightboxController.toggler,
			image: imageSrc,
		});
	};

	return (
		<SiteLayout hero>
			<PageMeta title={t("PAGE_TITLE")} description={t("META_DESCRIPTION")}>
				<PageMeta.OGP
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					locale={t("META_LOCALE")}
					type={t("META_TYPE")}
					url={t("META_URL")}
					image={t("META_IMAGE")}
					imageSecureUrl={t("META_IMAGE_SECURE")}
					siteName={t("META_SITE_NAME")}
					articlePublisher={t("META_ARTICLE_PUBLISHER")}
				/>
				<PageMeta.Twitter
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					site={t("META_TWITTER_SITE")}
					card={t("META_TWITTER_CARD")}
					image={t("META_TWITTER_IMAGE")}
					creator={t("META_TWITTER_CREATOR")}
				/>
				<PageMeta.Canonical href={t("META_CANONICAL")} />
				<PageMeta.Author content={t("META_AUTHOR")} />
			</PageMeta>
			<Hero>
				<HeroTitle>{t("HERO_TITLE")}</HeroTitle>
			</Hero>
			<VectorPageContainer>
				<VectorPageTitle>{t("ITS_EASY")}</VectorPageTitle>
				<VectorPageListWrapper>
					<VectorPageListItem>
						<Html>
							{t("LIST_ITEM_1", {
								listItemLink: "#Vector-NTI-File-Import",
							})}
						</Html>
					</VectorPageListItem>
					<VectorPageListItem>
						<Html>
							{t("LIST_ITEM_2", {
								listItemLink: "#Vector-NTI-Database-Conversion",
							})}
						</Html>
					</VectorPageListItem>
					<VectorPageListItem>{t("LIST_ITEM_3")}</VectorPageListItem>
				</VectorPageListWrapper>
				<VectorPageTryButtonWrapper>
					<VectorPageTryButton>{t("TRY_FOR_FREE")}</VectorPageTryButton>
				</VectorPageTryButtonWrapper>
			</VectorPageContainer>
			<Line utility={{ border: { bottom: { width: 1, color: "black--15" } } }} />
			<section id="Vector-NTI-File-Import">
				<VectorPageContainer>
					<VectorPageTitle>{t("FILE_IMPORT")}</VectorPageTitle>
					<VectorPageSubTitle>
						<Html Tag="span">
							{t("FILE_IMPORT_SUBTITLE", {
								fileSubTitleLink: "/features/convert-file-formats/vector-nti/",
							})}
						</Html>
					</VectorPageSubTitle>
					<VectorPageTable>
						<VectorPageTableItemWrapper>
							<VectorPageTableItem>{t("GP_LEFT_COL")}</VectorPageTableItem>
							<VectorPageTableItem>{t("GP_RIGHT_COL")}</VectorPageTableItem>
						</VectorPageTableItemWrapper>
						<VectorPageTableItemWrapper>
							<VectorPageTableItem>{t("MA4_LEFT_COL")}</VectorPageTableItem>
							<VectorPageTableItem>{t("MA4_RIGHT_COL")}</VectorPageTableItem>
						</VectorPageTableItemWrapper>
						<VectorPageTableItemWrapper>
							<VectorPageTableItem>{t("CEP_LEFT_COL")}</VectorPageTableItem>
							<VectorPageTableItem>{t("CEP_RIGHT_COL")}</VectorPageTableItem>
						</VectorPageTableItemWrapper>
						<VectorPageTableItemWrapper>
							<VectorPageTableItem>{t("APR_LEFT_COL")}</VectorPageTableItem>
							<VectorPageTableItem>{t("APR_RIGHT_COL")}</VectorPageTableItem>
						</VectorPageTableItemWrapper>
					</VectorPageTable>
					<VectorPageParagraphWrapper>
						<VectorPageParagraph>{t("SEQUENCE_DATA")}</VectorPageParagraph>
						<VectorPageParagraph Tag="span">
							<Html>{t("OLIGO_ARCHIVES")}</Html>
						</VectorPageParagraph>
						<VectorPageParagraph>{t("CLICK_ON_FILE_TYPES")}</VectorPageParagraph>
					</VectorPageParagraphWrapper>
					<VectorPageTabWrapper>
						<SmartTabs initialActiveTab="gen-bank">
							<Tabs className="mt-6">
								<SmartTabs.Tab id="gen-bank" href="#" cell="medium-4">
									{t("GEN_BANK_TITLE")}
								</SmartTabs.Tab>
								<SmartTabs.Tab id="contig-express" href="#" cell="medium-4">
									{t("CONTIG_EXPRESS_TITLE")}
								</SmartTabs.Tab>
								<SmartTabs.Tab id="alignx" href="#" cell="medium-4">
									{t("ALIGN_X_TITLE")}
								</SmartTabs.Tab>
							</Tabs>
							<Row className="tabs-content mt-10">
								<SmartTabs.TabBody.Mounted id="gen-bank" Component={Col}>
									<Row utility={{ justify: "center" }} spacing={{ mb: 7 }}>
										<Col sm={6}>
											<Paragraph sx={{ textAlign: "center" }}>{t("GEN_BANK_LEFT_COL")}</Paragraph>
										</Col>
										<Col sm={6}>
											<Paragraph sx={{ textAlign: "center" }}>{t("GEN_BANK_RIGHT_COL")}</Paragraph>
										</Col>
									</Row>
									<LightBoxItem
										preview={vntiGbPreview}
										full={vntiGbFull}
										openSlide={openLightboxOnSlide}
									/>
									<VectorPageParagraphWrapper>
										<VectorPageParagraph center>{t("GEN_BANK_BOTTOM_TEXT")}</VectorPageParagraph>
									</VectorPageParagraphWrapper>
								</SmartTabs.TabBody.Mounted>
								<SmartTabs.TabBody.Mounted
									id="contig-express"
									className="width-100"
									Component={Col}
								>
									<Row utility={{ justify: "center" }} spacing={{ mb: 7 }}>
										<Col sm={6}>
											<Paragraph sx={{ textAlign: "center" }}>
												{t("CONTIG_EXPRESS_LEFT_COL")}
											</Paragraph>
										</Col>
										<Col sm={6}>
											<Paragraph sx={{ textAlign: "center" }}>
												{t("CONTIG_EXPRESS_RIGHT_COL")}
											</Paragraph>
										</Col>
									</Row>
									<LightBoxItem
										preview={vntiCepPreview}
										full={vntiCepFull}
										openSlide={openLightboxOnSlide}
									/>
									<VectorPageParagraphWrapper>
										<VectorPageParagraph center>
											{t("CONTIG_EXPRESS_BOTTOM_TEXT")}
										</VectorPageParagraph>
									</VectorPageParagraphWrapper>
								</SmartTabs.TabBody.Mounted>
								<SmartTabs.TabBody.Mounted id="alignx" className="width-100" Component={Col}>
									<Row utility={{ justify: "center" }} spacing={{ mb: 7 }}>
										<Col sm={6}>
											<Paragraph sx={{ textAlign: "center" }}>{t("ALIGN_X_LEFT_COL")}</Paragraph>
										</Col>
										<Col sm={6}>
											<Paragraph sx={{ textAlign: "center" }}>{t("ALIGN_X_RIGHT_COL")}</Paragraph>
										</Col>
									</Row>
									<LightBoxItem
										preview={vntiAprPreview}
										full={vntiAprFull}
										openSlide={openLightboxOnSlide}
									/>
									<VectorPageParagraphWrapper>
										<VectorPageParagraph center>{t("ALIGN_X_BOTTOM_TEXT")}</VectorPageParagraph>
									</VectorPageParagraphWrapper>
								</SmartTabs.TabBody.Mounted>
							</Row>
						</SmartTabs>
					</VectorPageTabWrapper>
					<VectorPageVideoContainer>
						<WistiaVideo>
							<WistiaVideoEmbed id="3iuqgu3rtv" />
						</WistiaVideo>
					</VectorPageVideoContainer>
				</VectorPageContainer>
			</section>
			<Line utility={{ border: { bottom: { width: 1, color: "black--15" } } }} />
			<section id="Vector-NTI-Database-Conversion">
				<VectorPageContainer>
					<VectorPageTitle>{t("DATABASE_CONVERSION_TITLE")}</VectorPageTitle>
					<VectorPageParagraphWrapper>
						<VectorPageParagraph Tag="div">
							<Html>
								{t("DATABASE_CONVERSION_PARAGRAPH_1", {
									snapGeneCollection: "https://help.snapgene.com/m/user_guide/c/247314/",
								})}
							</Html>
						</VectorPageParagraph>
						<VectorPageParagraph>{t("DATABASE_CONVERSION_PARAGRAPH_2")}</VectorPageParagraph>
						<VectorPageParagraph Tag="div">
							<Html>
								{t("DATABASE_CONVERSION_PARAGRAPH_3", {
									parentDescendantRelationships: "/features/convert-file-formats/vector-nti/",
								})}
							</Html>
						</VectorPageParagraph>
						<VectorPageParagraph>{t("DATABASE_CONVERSION_PARAGRAPH_4")}</VectorPageParagraph>
					</VectorPageParagraphWrapper>
					<VectorPageTabWrapper>
						<SmartTabs initialActiveTab="vectorAdvance">
							<Tabs className="mt-6">
								<SmartTabs.Tab id="vectorAdvance" href="#" cell="medium-6">
									{t("ADVANCE_TAB")}
								</SmartTabs.Tab>
								<SmartTabs.Tab id="vectorExpress" href="#" cell="medium-6">
									{t("EXPRESS_TAB")}
								</SmartTabs.Tab>
							</Tabs>
							<Row className="tabs-content mt-10">
								<SmartTabs.TabBody.Mounted id="vectorAdvance" Component={Col}>
									<LightBoxItem
										preview={vntiAdvancePreview}
										full={vntiAdvanceFull}
										openSlide={openLightboxOnSlide}
									/>
									<VectorPageParagraphWrapper>
										<VectorPageParagraph center>{t("ADVANCE_TAB_BOTTOM_TEXT")}</VectorPageParagraph>
									</VectorPageParagraphWrapper>
								</SmartTabs.TabBody.Mounted>
								<SmartTabs.TabBody.Mounted id="vectorExpress" className="width-100" Component={Col}>
									<LightBoxItem
										preview={vntiExpressPreview}
										full={vntiExpressFull}
										openSlide={openLightboxOnSlide}
									/>
									<VectorPageParagraphWrapper>
										<VectorPageParagraph center>{t("EXPRESS_TAB_BOTTOM_TEXT")}</VectorPageParagraph>
									</VectorPageParagraphWrapper>
								</SmartTabs.TabBody.Mounted>
							</Row>
						</SmartTabs>
					</VectorPageTabWrapper>
					<VectorPageVideoContainer>
						<WistiaVideo>
							<WistiaVideoEmbed id="7fzojeqwzi" />
						</WistiaVideo>
					</VectorPageVideoContainer>
				</VectorPageContainer>
			</section>
			<Line utility={{ border: { bottom: { width: 1, color: "black--15" } } }} />
			<VectorPageContainer>
				<VectorPageTitle>{t("MAP_TITLE")}</VectorPageTitle>
				<VectorPageImageWrapper>
					<Image src={map} className="width-100 p-3" />
				</VectorPageImageWrapper>
				<BlockQuote>
					<BlockQuote.Head>
						<BlockQuoteItem
							active
							author={t("BLOCK_QUOTE_AUTHOR")}
							institution={t("BLOCK_QUOTE_INSTITUTION")}
						>
							{t("BLOCK_QUOTE_TEXT")}
						</BlockQuoteItem>
					</BlockQuote.Head>
				</BlockQuote>
			</VectorPageContainer>
			<VectorPageContactUs>
				<Html Tag="span">
					{t("CONTACT_US", {
						contactUs:
							"https://support.snapgene.com/hc/en-us/requests/new?ticket_form_id=360000552331",
					})}
				</Html>
			</VectorPageContactUs>
			<FsLightbox toggler={lightboxController.toggler} sources={[lightboxController.image]} />
		</SiteLayout>
	);
};

export default pipe(VectorNTI, WithLocale({ resources }));
